/**
 * @typedef UpdateJobPermissionsBehaviorOptions
 * @property {string} [updatePath] - The path for the reference where the update will be applied. The relative path to `job_permissions`
 * will be calculated from this. This option is only used when `update` is provided.
 * @property {boolean} [onlyUpdateListLocation] - Only update the list location under `job_permissions` and not the jobs location.
 * @property {boolean} [wipeExistingData] - Set the entire `permissionsData` object at the jobs location instead of path updates. The same
 * will be applied for the list update. If `permissionsData` is null, this will be true.
 * @property {string[]} [wipeExistingDataAtPaths] - Provide a list of keys that should not be flattened so that the data for that key will
 */

/**
 * @typedef UpdateJobPermissionsOptions
 * @property {Object} [update] - The update object to apply the updates to. If omitted, the update will run automatically.
 * @property {Object} [firebase] - A firebase app to use for just-in-time reads and updates. If omitted, falls back to FirebaseWorker.
 * @property {Record<string,boolean>|string[]} [sharing] - An object of the company keys to update. `mainCompanyId` will be added to this
 * automatically, unless prevented with `skipAddingMainCompanyToSharing`.
 * @property {boolean} [fetchSharedCompanies] - Automatically fetch the sharing list from the job to use as `options.sharing`.
 * @property {boolean} [skipAddingMainCompanyToSharing] - Skip automatically adding the `mainCompanyId` to the sharing list.
 * @property {Object} [jobChooserOptions] - Used to decide which metadata to sync based on the options. If not provided and there is a
 * `mainCompanyId`, the option will be fetched from the database (`photoheight/company_space/<mainCompanyId>/options/job_chooser`).
 */

/**
 * Update metadata and/or permissions data synchronously between the `/job_permissions/jobs` and `/job_permissions/list` locations
 * @param {String} jobId - The key of the job to update permissions for.
 * @param {String} mainCompanyId - The key of the main company to apply this permissions update for. This is also used to determine the
 * metadata to update based on the job chooser options.
 * @param {Object} permissionsData - An object of key/value pairs for the changes that should be made to job permissions.
 * @param {UpdateJobPermissionsOptions & UpdateJobPermissionsBehaviorOptions} [options] - Options for the update operation.
 * @returns {Promise<void>} A promise that resolves when either the updates have been appended to the provided update object or the
 * update is complete.
 */
export async function UpdateJobPermissions(jobId, mainCompanyId, permissionsData, options = {}) {
  // If no update object is provided, then we should run the update locally when done
  const shouldRunUpdate = options.update == null;
  // If we're running the update locally, the update path must be empty
  if (shouldRunUpdate) options.updatePath = '';

  // Make sure we have an update object to work with
  options.update ??= {};

  // Get defaults for firebase and the update object
  const database = options.firebase ? options.firebase.database() : globalThis.FirebaseWorker.database();

  // Set the jobChooserOptions to the options version by default. If there is none, check if a mainCompanyId is provided, and if so, fetch
  // the data from Firebase
  options.jobChooserOptions ??=
    mainCompanyId != null
      ? (await database.ref(`photoheight/company_space/${mainCompanyId}/options/job_chooser`).once('value')).val() || null
      : null;

  // Set wipeExistingData if permissionsData is null
  if (permissionsData == null) options.wipeExistingData = true;

  // Get the companies that we should update. If fetchSharedCompanies is set, then fetch the shared list from the job.
  const sharing = options.fetchSharedCompanies
    ? (await database.ref(`photoheight/jobs/${jobId}/sharing`).once('value')).val() || {}
    : JSON.parse(JSON.stringify(options.sharing || {}));

  // Get the list of companies to update
  const companiesToUpdate = Array.isArray(sharing) ? sharing : Object.keys(sharing);
  // If a mainCompanyId is provided, add it to the list of companies to update
  if (mainCompanyId && !options.skipAddingMainCompanyToSharing && !companiesToUpdate.includes(mainCompanyId)) {
    companiesToUpdate.push(mainCompanyId);
  }

  // Get the full update object
  const jobPermissionsUpdate = composeJobPermissionsUpdate(jobId, permissionsData, companiesToUpdate, options);

  // Apply the update object to the main update object
  Object.assign(options.update, jobPermissionsUpdate);

  // If we're running the update locally, do it now and wait for it to finish
  if (shouldRunUpdate) await database.ref().update(options.update);
}

/**
 * @typedef ComposeJobPermissionsUpdateOptions
 * @property {object} [jobChooserOptions] - The job chooser options for the main company. This is used to determine which metadata to sync,
 * and should always be provided if `mainCompanyId` is provided. (`photoheight/company_space/<mainCompanyId>/options/job_chooser`)
 */

/**
 * Compose a multi-location update object for job permissions.
 * @param {string} jobId - The key of the job to update permissions for.
 * @param {object} permissionsData - An object of key/value pairs for the changes that should be made to job permissions.
 * @param {string[]} companiesToUpdate - A list of company keys to update the permissions for.
 * @param {ComposeJobPermissionsUpdateOptions & UpdateJobPermissionsBehaviorOptions} [options] - Options for the update operation.
 * @returns {Record<string, any>} A multi-location update object.
 */
export function composeJobPermissionsUpdate(jobId, permissionsData, companiesToUpdate, options = {}) {
  // Extract options from the options object
  const { updatePath, wipeExistingData, wipeExistingDataAtPaths, jobChooserOptions, onlyUpdateListLocation } = options;

  // Make sure the update path (if provided) is clean
  const cleanUpdatePath = cleanPath(updatePath || '');

  // Determine the path to job_permissions based off the updatePath
  const permissionsPath = reducePath('photoheight/job_permissions', cleanUpdatePath);

  // Flatten the data object so everything is path-based
  const flattenedPermissionsData = flattenObject(permissionsData, { wipeExistingDataAtPaths });
  cleanUpdate(flattenedPermissionsData);

  // Get the update object for the list section of job_permissions
  const jobListUpdate = getJobListUpdateFromJobData(permissionsData, { wipeExistingDataAtPaths, jobChooserOptions });
  // Get a flattened version of the jobListUpdate
  const flattenedJobListUpdate = flattenObject(jobListUpdate, { wipeExistingDataAtPaths });
  cleanUpdate(flattenedJobListUpdate);

  // Create an object to hold the multi-location update
  const update = {};

  // Build the update for each company in sharing
  for (const sharedCompanyId of companiesToUpdate) {
    // Get the base path to the permissions locations
    const jobsBasePath = cleanPath([permissionsPath, sharedCompanyId, 'jobs', jobId].join('/'));
    const listBasePath = cleanPath([permissionsPath, sharedCompanyId, 'list', jobId].join('/'));
    // Check if the options say we should wipe out existing data
    if (wipeExistingData || permissionsData == null) {
      // Set the entire permissionsData object at the jobs base path
      if (!onlyUpdateListLocation) update[jobsBasePath] = permissionsData;
      // Set the entire jobListUpdate object at the list base path
      update[listBasePath] = jobListUpdate;
    }
    // Otherwise, add the flattened updates to the update object
    else {
      if (!onlyUpdateListLocation) {
        // Apply each part of the update to the jobs part of job_permissions
        for (let updatePath in flattenedPermissionsData) {
          update[[jobsBasePath, updatePath].join('/')] = flattenedPermissionsData[updatePath];
        }
      }
      // Apply each part of the job list update to the list part of job_permissions
      for (let updatePath in flattenedJobListUpdate) {
        update[[listBasePath, updatePath].join('/')] = flattenedJobListUpdate[updatePath];
      }
    }
  }

  // Return the update object
  return update;
}

// TODO (2024-05-15): This function returns an empty object instead of `null` when `permissionsData` is `null`. The `/jobs` location is
// updated with `null` instead. This inconsistency should be fixed.
/**
 * Get a job list update object from job data.
 * @param {Object} permissionsData - The data for job permissions.
 * This may be the actual job permissions data or an update object that will be applied to job permissions.
 * @param {Object} [options] - Options for the update operation.
 * @param {string[]} [options.wipeExistingDataAtPaths] - A list of keys that should not be flattened so that the data for that key will
 * be set at the path directly instead of being flattened.
 * @param {Object} [options.jobChooserOptions] - The job chooser options for the main company.
 * @returns {Object} A job list update object.
 */
const getJobListUpdateFromJobData = (permissionsData, options = {}) => {
  const { wipeExistingDataAtPaths, jobChooserOptions } = options;

  let update = {};
  if (!permissionsData) return update;

  // Loop required job list keys and copy values from job data to update.
  ['name', 'status', 'published', 'date_shared'].forEach((key) => {
    let val = permissionsData[key];
    if (val !== undefined && !Number.isNaN(val)) update[key] = val;
  });

  // Check if we should reset metadata before setting data
  if (wipeExistingDataAtPaths?.includes('metadata')) {
    /** @type {Object|null} */
    update.metadata = {};
  }

  // Check if the update is trying to delete metadata altogether
  if (permissionsData.metadata === null) {
    update.metadata = null;
  } else {
    if (jobChooserOptions) {
      // Build and run through a list of the metadata attributes that should be included in the job list update
      [jobChooserOptions.job_chooser_checkmark?.attribute, jobChooserOptions.limit_job_chooser?.property]
        .filter(Boolean)
        .forEach((metadataAttribute) => {
          // Determine where we should get the value from - either from a normal update object or a path based update object
          let val = permissionsData.metadata
            ? permissionsData.metadata[metadataAttribute]
            : permissionsData[`metadata/${metadataAttribute}`];

          // If the value is not undefined, add it to the update object
          if (val !== undefined) {
            if (!update.metadata) update.metadata = {};
            update.metadata[metadataAttribute] = val;
          }
        });
    }
  }
  return update;
};

// Cleans up update objects that try to set .sv or .priority since they can't be part of a path-based update
const cleanUpdate = (update) => {
  let invalidPathEndings = ['.sv', '.priority'];
  for (let path in update) {
    for (let invalidPathEnding of invalidPathEndings) {
      if (path.endsWith(`/${invalidPathEnding}`)) {
        let newPath = path.replace(`/${invalidPathEnding}`, '');
        update[newPath] = { [invalidPathEnding]: update[path] };
        delete update[path];
      }
    }
  }
};

const cleanPath = (path) => {
  return path.replace(/(^\/+|\/+$)/g, '');
};

const reducePath = (path, pathToRemove) => {
  pathToRemove
    .split('/')
    .reverse()
    .forEach((pathPart) => {
      path = path.replace(pathPart, '');
    });
  return cleanPath(path);
};

const flattenObject = (data, options) => {
  options = options || {};
  options.wipeExistingDataAtPaths = options.wipeExistingDataAtPaths || [];
  let temp = {};
  for (let key in data) {
    if (data[key] && typeof data[key] === 'object' && !options.wipeExistingDataAtPaths.includes(key)) {
      let flattenedData = flattenObject(data[key], options);
      for (let path in flattenedData) temp[key + '/' + path] = flattenedData[path];
    } else {
      temp[key] = data[key];
    }
  }
  return temp;
};
